import { Formik, FormikValues } from 'formik';
import React, { ChangeEvent, useContext, useState, useEffect } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from './styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { getProjectErrorFromRawError, isProjectError } from '../../../utils/errors';
import { ProjectErrorType } from '../../../utils/errors/types';
import { useNavigate } from 'react-router-dom';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { Author } from '../../../api/types/author';
import { MinimalAuthorSchema } from '../../../api/author/parser';
import { CreateAuthorDto } from '../../../api/typeDtos/author';
import { createAuthor, updateAuthor } from '../../../api/author';
import { getFileExtension } from '../../../utils/s3/utils';
import { PUBLIC_BUCKET } from '../../../defines';
import { uploadPublicFile } from '../../../utils/s3';
import { getAuthorUploadFileName } from '../../../api/author/utils';
import { ReactComponent as AccessLinkIcon } from '../../../assets/icons/icon-grey-check-square-offset.svg';
import ProjectTextArea from '../../inputs/ProjectTextArea';
import ProjectCalendarPicker from '../../dateAndTime/ProjectCalendarPicker';
import ProjectImageField from '../../inputs/ProjectImageField';
import ProjectSearchDropdown from '../../ProjectSearchDropdown';
import ProjectPicker from '../../ProjectPicker';
import useSearchDropdown, { SearchDropdownItem } from '../../../hooks/useSearchDropdown';
import { AuthorCategory } from '../../../api/types/contentManagement';
import { getAllAuthorCategories } from '../../../api/contentManagement';
import { AuthorContent } from '../../../screens/ContentManagement/components/Authors/authorContent';
import { ContentGroup } from '../../../api/types/contentGroup';
import { Lesson } from '../../../api/types/lesson';
import { getContentGroupById, updateContentGroup } from '../../../api/contentGroup';
import { updateLesson } from '../../../api/lesson';
import ContentPickerModal from './ContentPickerModal';
import CreateOrEditLessonModal from '../CreateOrEditLessonModal';
import CreateOrEditMentorShortModal from '../CreateOrEditMentorShortModal';

type ContentActions = {
  onAssignCourse: () => void;
  onNewCourse: () => void;
  onAddLesson: (group: ContentGroup, isQa: boolean) => void;
  onCreateLesson: (group: ContentGroup, isQa: boolean) => void;
  onAddShort: () => void;
};

const initialValues = {
  name: '',
  bio: '',
  releaseDate: undefined,
  isActive: true,
  isFeatured: false,
  isNew: false,
  description: '',
  learnFromMentor: '',
  categoryId: undefined,
};

const ModalBody = ({
  modalState,
  content,
  contentActions,
  onSave,
  onCancel,
}: {
  modalState?: Author;
  content?: AuthorContent;
  contentActions: ContentActions;
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);

  const [imagePath, setImagePath] = useState<string | undefined>(modalState?.imagePath || undefined);
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [imageUploadProgress, setImageUploadProgress] = useState<number | undefined>(undefined);
  const [customThumbnailImagePath, setCustomThumbnailImagePath] = useState<string | undefined>(
    modalState?.customThumbnailImagePath || undefined,
  );
  const [categories, setCategories] = useState<AuthorCategory[]>([]);

  const categoryDropdownProps = useSearchDropdown({
    items: categories.map((category) => ({
      value: category.id,
      label: category.name,
      searchLabel: category.name,
    })),
    singleSelect: true,
    dropdownType: 'basic',
  });

  useEffect(() => {
    const fetchCategories = async () => {
      const result = await getAllAuthorCategories();
      if (result && Array.isArray(result)) {
        setCategories(result);
      }
    };
    fetchCategories();
  }, []);

  // Set initial selected category when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && modalState?.categoryId) {
      const selectedCategory = categories.find((cat) => cat.id === modalState.categoryId);
      if (selectedCategory) {
        categoryDropdownProps.setSelectedItems([
          {
            value: selectedCategory.id,
            label: selectedCategory.name,
            searchLabel: selectedCategory.name,
          },
        ]);
      }
    }
  }, [categories, modalState?.categoryId]);

  const authorInitialValues = {
    name: modalState?.name,
    bio: modalState?.bio,
    description: modalState?.description,
    learnFromMentor: modalState?.learnFromMentor,
    releaseDate: modalState?.releaseDate,
    isActive: modalState?.isActive !== false,
    isFeatured: modalState?.isFeatured,
    isNew: modalState?.isNew,
    categoryId: modalState?.categoryId,
  };

  const _onSubmit = async (values: FormikValues) => {
    await onSave?.({
      ...values,
      imagePath: imagePath,
      customThumbnailImagePath: customThumbnailImagePath,
      categoryId: categoryDropdownProps.selectedItems?.[0]?.value || null,
    });
  };

  const onUploadPublicFile = async (e: ChangeEvent<HTMLInputElement> | undefined, fileName: string, path: string) => {
    setIsLoading(true);
    const file = e?.target?.files?.[0];
    // @ts-ignore
    const newFile = new File([file], fileName, { type: file?.type });
    if (newFile) {
      if (e && e.target && e.target.value) {
        e.target.value = '';
      }

      await uploadPublicFile(newFile, path, (progress) => {
        setImageUploadProgress(progress);
        if (progress === 100) {
          setImagePath(path);
          const link = PUBLIC_BUCKET + path;
          setImageUrl(link);
          setImageUploadProgress(undefined);
        }
      });
    }
    setIsLoading(false);
  };

  const selectHandlerForImageFile = async (event: React.ChangeEvent<HTMLInputElement> | undefined) => {
    const file = event?.target?.files?.[0];

    if (file) {
      const extension = getFileExtension(file);
      const fileName = getAuthorUploadFileName();
      const path = 'images/'.concat(fileName).concat('.', extension || '');
      await onUploadPublicFile(event, fileName, path);
    }
  };

  const onAccessFileLink = () => {
    const _imageUrl = imageUrl || PUBLIC_BUCKET + imagePath;
    window.open(_imageUrl, '_blank');
  };

  const onClearImageLink = () => {
    setImagePath(undefined);
  };

  return (
    <Formik
      initialValues={modalState ? authorInitialValues : initialValues}
      validationSchema={MinimalAuthorSchema}
      onSubmit={_onSubmit}>
      {({ values, handleChange, errors, touched, setFieldValue }) => {
        const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const { name, checked } = e.target;
          setFieldValue(name, checked);
        };
        return (
          <Components.Form>
            <Components.ModalBodyContainer>
              <Components.FieldsWrapper>
                <Components.FieldStartAdornment variant={'minimal'} />
                <Components.FieldsContent>
                  <Components.FieldWrapper>
                    <Components.FieldLabel>Value</Components.FieldLabel>
                    <ProjectTextField
                      value={values?.name}
                      onChangeText={handleChange}
                      name={'name'}
                      errorMessage={errors.name && touched.name ? errors?.name : undefined}
                      placeholder={'Enter mentor name'}
                    />
                  </Components.FieldWrapper>
                </Components.FieldsContent>
              </Components.FieldsWrapper>

              <Components.FieldsWrapper>
                <Components.FieldStartAdornment variant={'additional'} />
                <Components.FieldsContent>
                  <Components.FieldWrapper>
                    <Components.FieldLabel>Image</Components.FieldLabel>
                    <Components.UploadFileWrapper>
                      <Components.InputMenu
                        type='file'
                        accept={'image/*'}
                        id='imageFile'
                        onChange={selectHandlerForImageFile}
                      />
                      <ProjectTextField value={imagePath || ''} disabled placeholder={'No image path'} />
                      {imagePath ? (
                        <>
                          <Components.IconWrapper onClick={() => onAccessFileLink()}>
                            <AccessLinkIcon />
                          </Components.IconWrapper>
                          <Components.ClearButton onClick={onClearImageLink}>Clear</Components.ClearButton>
                        </>
                      ) : (
                        <Components.UploadButton htmlFor={'imageFile'}>
                          {typeof imageUploadProgress === 'number' ? `${imageUploadProgress}%` : 'Upload Image'}
                        </Components.UploadButton>
                      )}
                    </Components.UploadFileWrapper>
                  </Components.FieldWrapper>

                  <Components.FieldWrapper>
                    <Components.FieldLabel>Bio</Components.FieldLabel>
                    <ProjectTextArea
                      value={values.bio}
                      onChangeText={handleChange}
                      name={'bio'}
                      errorMessage={errors.bio && touched.bio ? errors?.bio : undefined}
                      placeholder={`Enter mentor's bio`}
                      rows={10}
                    />
                  </Components.FieldWrapper>

                  <Components.FieldWrapper>
                    <Components.FieldLabel>Custom card short description (max 100 characters)</Components.FieldLabel>
                    <ProjectTextField
                      value={values.description}
                      onChangeText={handleChange}
                      name={'description'}
                      errorMessage={errors.description && touched.description ? errors?.description : undefined}
                      placeholder={'Enter mentor description on card'}
                    />
                  </Components.FieldWrapper>

                  <Components.FieldWrapper>
                    <Components.FieldLabel>Learn from this mentor</Components.FieldLabel>
                    <ProjectTextArea
                      value={values.learnFromMentor}
                      onChangeText={handleChange}
                      name={'learnFromMentor'}
                      placeholder={'e.g. How to validate an idea before building'}
                      rows={3}
                    />
                    <Components.FieldBasic>
                      Shown under the mentor&apos;s name in the &apos;Learn from world-class mentors&apos; panel.
                    </Components.FieldBasic>
                  </Components.FieldWrapper>

                  <Components.FieldWrapper>
                    <Components.FieldLabel>Category</Components.FieldLabel>
                    <ProjectPicker dropdownProps={categoryDropdownProps} notSetText={'Select a category'} />
                    <ProjectSearchDropdown {...categoryDropdownProps} />
                  </Components.FieldWrapper>

                  <Components.FieldWrapper>
                    <Components.FieldLabel>Custom Carousel Image</Components.FieldLabel>
                    <ProjectImageField
                      imagePath={customThumbnailImagePath}
                      setImagePath={setCustomThumbnailImagePath}
                      id={'customThumbnailImageFile'}
                    />
                  </Components.FieldWrapper>

                  <Components.FieldWrapper>
                    <Components.DateFieldsWrapper>
                      <Components.DateFieldWrapper>
                        <Components.FieldLabel>Release date</Components.FieldLabel>
                        <ProjectCalendarPicker
                          dateValue={values?.releaseDate ? new Date(values?.releaseDate) : null}
                          onDateValueChange={(value) => {
                            setFieldValue('releaseDate', value?.getTime());
                          }}
                          valueDisplayFormat={'day'}
                        />
                      </Components.DateFieldWrapper>
                      {values?.releaseDate && (
                        <Components.ClearButton
                          onClick={() => {
                            setFieldValue('releaseDate', undefined);
                          }}>
                          Clear release date
                        </Components.ClearButton>
                      )}
                    </Components.DateFieldsWrapper>
                  </Components.FieldWrapper>

                  <Components.FieldWrapper>
                    <Components.FieldLabel>Status</Components.FieldLabel>
                    <Components.CheckboxFieldWrapper>
                      <Components.Checkbox checked={!!values?.isActive} onChange={onCheckboxChange} name='isActive' />
                      <Components.CheckboxLabel>Is Active</Components.CheckboxLabel>
                    </Components.CheckboxFieldWrapper>
                    <Components.CheckboxFieldWrapper>
                      <Components.Checkbox checked={!!values?.isNew} onChange={onCheckboxChange} name='isNew' />
                      <Components.CheckboxLabel>Is New Mentor</Components.CheckboxLabel>
                    </Components.CheckboxFieldWrapper>
                  </Components.FieldWrapper>

                  {modalState?.id && content && (
                    <Components.ContentDetails>
                      <Components.ContentSummary>
                        Content ({content.coursesCount} courses, {content.qaCount} Q&A, {content.shortsCount} shorts)
                      </Components.ContentSummary>
                      <Components.ContentScroll>
                        <Components.ContentBlock>
                          <Components.ContentHeaderRow>
                            <Components.FieldLabel>Courses</Components.FieldLabel>
                            <Components.ContentActions>
                              <Components.ContentActionButton type={'button'} onClick={contentActions.onAssignCourse}>
                                Assign course
                              </Components.ContentActionButton>
                              <Components.ContentActionButton type={'button'} onClick={contentActions.onNewCourse}>
                                New course
                              </Components.ContentActionButton>
                            </Components.ContentActions>
                          </Components.ContentHeaderRow>
                          {content.courses.length === 0 && (
                            <Components.ContentEmpty>No courses yet</Components.ContentEmpty>
                          )}
                          {content.courses.map((course) => {
                            const date = course.group.publishedAt || course.group.releaseAt;
                            return (
                              <Components.ContentBlock key={course.group.id}>
                                <Components.FieldLabelBigger>
                                  {course.group.title} ({course.lessons.length} lessons
                                  {date ? `, ${new Date(date).toLocaleDateString()}` : ''})
                                </Components.FieldLabelBigger>
                                <Components.ContentSubList>
                                  <Components.ContentActions>
                                    <Components.ContentActionButton
                                      type={'button'}
                                      onClick={() => contentActions.onAddLesson(course.group, false)}>
                                      Add lesson
                                    </Components.ContentActionButton>
                                    <Components.ContentActionButton
                                      type={'button'}
                                      onClick={() => contentActions.onCreateLesson(course.group, false)}>
                                      Create lesson
                                    </Components.ContentActionButton>
                                  </Components.ContentActions>
                                  {course.lessons.length > 0 && (
                                    <Components.ContentList>
                                      {course.lessons.map((lesson) => (
                                        <li key={lesson.id}>{lesson.title}</li>
                                      ))}
                                    </Components.ContentList>
                                  )}
                                  <Components.ContentHeaderRow>
                                    <Components.FieldLabel>BONUS</Components.FieldLabel>
                                    <Components.ContentActions>
                                      <Components.ContentActionButton
                                        type={'button'}
                                        onClick={() => contentActions.onAddLesson(course.group, true)}>
                                        Add Q&A
                                      </Components.ContentActionButton>
                                      <Components.ContentActionButton
                                        type={'button'}
                                        onClick={() => contentActions.onCreateLesson(course.group, true)}>
                                        Create Q&A
                                      </Components.ContentActionButton>
                                    </Components.ContentActions>
                                  </Components.ContentHeaderRow>
                                  {course.qaLessons.length === 0 ? (
                                    <Components.ContentEmpty>No Q&A yet</Components.ContentEmpty>
                                  ) : (
                                    <Components.ContentList>
                                      {course.qaLessons.map((lesson) => (
                                        <li key={lesson.id}>{lesson.title}</li>
                                      ))}
                                    </Components.ContentList>
                                  )}
                                </Components.ContentSubList>
                              </Components.ContentBlock>
                            );
                          })}
                        </Components.ContentBlock>
                        <Components.ContentBlock>
                          <Components.ContentHeaderRow>
                            <Components.FieldLabel>Shorts</Components.FieldLabel>
                            <Components.ContentActionButton type={'button'} onClick={contentActions.onAddShort}>
                              Add short
                            </Components.ContentActionButton>
                          </Components.ContentHeaderRow>
                          {content.shorts.length === 0 ? (
                            <Components.ContentEmpty>No shorts yet</Components.ContentEmpty>
                          ) : (
                            <Components.ContentList>
                              {content.shorts.map((short) => (
                                <li key={short.id}>{short.title}</li>
                              ))}
                            </Components.ContentList>
                          )}
                        </Components.ContentBlock>
                      </Components.ContentScroll>
                    </Components.ContentDetails>
                  )}
                </Components.FieldsContent>
              </Components.FieldsWrapper>
            </Components.ModalBodyContainer>
            <Components.ModalButtonsContainer>
              <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
              <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
            </Components.ModalButtonsContainer>
          </Components.Form>
        );
      }}
    </Formik>
  );
};

type LessonTarget = { group: ContentGroup; isQa: boolean };

const CreateOrEditAuthorModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
  content,
  groups = [],
  lessons = [],
  authors = [],
  onContentChanged,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { author?: Author };
  refreshCallback?: () => Promise<void>;
  // Live content of the edited author, so it refreshes after content is added.
  content?: AuthorContent;
  groups?: ContentGroup[];
  lessons?: Lesson[];
  authors?: Author[];
  onContentChanged?: () => Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast, showWarningToast } = useContext(ToastContext);
  const navigate = useNavigate();

  const confirmationModal = useModalState<boolean>();

  // The child modals stack on top of this one (MUI modals stack; ConfirmationModal already relies on it).
  const assignCourseModal = useModalState<boolean>();
  const reassignModal = useModalState<ContentGroup>();
  const pickLessonModal = useModalState<LessonTarget>();
  const createLessonModal = useModalState<LessonTarget>();
  const shortModal = useModalState<boolean>();

  const authorId = modalState?.author?.id;

  const runContentUpdate = async (action: () => Promise<unknown>, successText: string): Promise<boolean> => {
    setIsLoading(true);
    const result = await action();
    setIsLoading(false);
    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
      return false;
    }
    showSuccessToast(successText);
    await onContentChanged?.();
    return true;
  };

  const assignCourse = async (group: ContentGroup) => {
    if (!authorId) {
      return;
    }
    const ok = await runContentUpdate(
      () => updateContentGroup({ id: group.id, authorId }),
      'The course was assigned to this mentor',
    );
    if (ok) {
      assignCourseModal.onModalClose();
    }
  };

  const onPickCourse = (groupId: string) => {
    const group = groups.find((item) => item.id === groupId);
    if (!group) {
      return;
    }
    if (group.authorId && group.authorId !== authorId) {
      reassignModal.open(group);
    } else {
      assignCourse(group).then();
    }
  };

  // Appends a lesson id to a course. The group is re-read first so a stale list can never drop or duplicate items.
  const appendLessonToCourse = async (groupId: string, lessonId: string): Promise<ProjectErrorType | undefined> => {
    const freshGroup = await getContentGroupById(groupId);
    if (!freshGroup) {
      return getProjectErrorFromRawError(new Error('The course could not be loaded'));
    }
    const itemIds = freshGroup.itemIds || [];
    if (itemIds.includes(lessonId)) {
      return undefined;
    }
    const result = await updateContentGroup({ id: groupId, itemIds: [...itemIds, lessonId] });
    return isProjectError(result) || undefined;
  };

  const onPickLesson = async (lessonId: string) => {
    const target = pickLessonModal.modalState;
    const lesson = lessons.find((item) => item.id === lessonId);
    if (!target || !lesson) {
      return;
    }
    const alreadyInCourse = (target.group.itemIds || []).includes(lessonId);
    if (alreadyInCourse && (!target.isQa || lesson.isQuestionAndAnswer)) {
      showWarningToast('This lesson is already in the course');
      return;
    }
    const ok = await runContentUpdate(
      async () => {
        if (target.isQa && !lesson.isQuestionAndAnswer) {
          const lessonResult = await updateLesson({ id: lesson.id, isQuestionAndAnswer: true });
          if (isProjectError(lessonResult)) {
            return lessonResult;
          }
        }
        return (await appendLessonToCourse(target.group.id, lessonId)) || { success: true };
      },
      target.isQa ? 'The Q&A was added to the course' : 'The lesson was added to the course',
    );
    if (ok) {
      pickLessonModal.onModalClose();
    }
  };

  const onLessonCreated = async (lessonId: string) => {
    const target = createLessonModal.modalState;
    if (!target) {
      return;
    }
    await runContentUpdate(
      async () => (await appendLessonToCourse(target.group.id, lessonId)) || { success: true },
      target.isQa ? 'The Q&A was added to the course' : 'The lesson was added to the course',
    );
  };

  const onShortModalClose = (refreshTrigger?: boolean) => {
    shortModal.onModalClose();
    if (refreshTrigger) {
      onContentChanged?.().then();
    }
  };

  const availableCourses = groups
    .filter((group) => group.type === 'collection' && group.authorId !== authorId)
    .sort((a, b) => Number(!!a.authorId) - Number(!!b.authorId) || a.title.localeCompare(b.title))
    .map((group) => {
      const owner = group.authorId ? authors.find((item) => item.id === group.authorId)?.name || group.authorId : null;
      const label = owner ? `${group.title} (owner: ${owner})` : `${group.title} (no mentor)`;
      return { value: group.id, label, searchLabel: label.toLowerCase() };
    });

  const lessonTarget = pickLessonModal.modalState;
  const availableLessons = lessonTarget
    ? lessons
        .filter((lesson) =>
          lessonTarget.isQa
            ? !(lessonTarget.group.itemIds || []).includes(lesson.id) || !lesson.isQuestionAndAnswer
            : !(lessonTarget.group.itemIds || []).includes(lesson.id) && !lesson.isQuestionAndAnswer,
        )
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((lesson) => ({ value: lesson.id, label: lesson.title, searchLabel: lesson.title.toLowerCase() }))
    : [];

  const contentActions: ContentActions = {
    onAssignCourse: () => assignCourseModal.open(true),
    onNewCourse: () => navigate(`/content-groups/manage?content-group-type=collection&authorId=${authorId}`),
    onAddLesson: (group, isQa) => pickLessonModal.open({ group, isQa }),
    onCreateLesson: (group, isQa) => createLessonModal.open({ group, isQa }),
    onAddShort: () => shortModal.open(true),
  };

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const data: CreateAuthorDto = {
      name: values?.name,
      imagePath: values?.imagePath || null,
      customThumbnailImagePath: values?.customThumbnailImagePath || null,
      bio: values?.bio?.length ? values?.bio : null,
      description: values?.description?.length ? values?.description : null,
      learnFromMentor: values?.learnFromMentor?.trim() || null,
      releaseDate: values?.releaseDate || null,
      isActive: values?.isActive !== false,
      isFeatured: values?.isFeatured,
      isNew: values?.isNew,
      categoryId: values?.categoryId || null,
    };

    setIsLoading(true);
    const result =
      modalState && modalState?.author?.id
        ? await updateAuthor({ ...data, id: modalState?.author?.id })
        : await createAuthor(data);
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.author?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a mentor');
      onModalClose?.(true);
      refreshCallback?.().then();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={
          <ModalBody
            modalState={modalState?.author}
            content={content}
            contentActions={contentActions}
            onSave={_onSave}
            onCancel={_onModalClose}
          />
        }
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={modalState?.author ? `Edit mentor` : 'Create new mentor'}
        titleSuffix={modalState?.author ? `(ID: ${modalState?.author?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
      <ContentPickerModal
        isModalOpen={assignCourseModal.isModalOpen}
        onModalClose={assignCourseModal.onModalClose}
        title={'Assign course'}
        description={'Courses owned by another mentor are listed with their owner and ask for confirmation.'}
        items={availableCourses}
        placeholder={'Select a course'}
        confirmLabel={'Assign'}
        onConfirm={onPickCourse}
      />
      <ConfirmationModal
        {...reassignModal}
        confirmationText={(group?: ContentGroup) =>
          `"${group?.title}" already belongs to another mentor. Reassign it to this mentor?`
        }
        onConfirm={(group?: ContentGroup) => group && assignCourse(group).then()}
      />
      <ContentPickerModal
        isModalOpen={pickLessonModal.isModalOpen}
        onModalClose={pickLessonModal.onModalClose}
        title={pickLessonModal.modalState?.isQa ? 'Add Q&A lesson' : 'Add lesson'}
        description={
          pickLessonModal.modalState?.isQa
            ? `Adds an existing lesson to "${pickLessonModal.modalState?.group.title}" and marks it as Q&A.`
            : `Adds an existing lesson to "${pickLessonModal.modalState?.group.title}".`
        }
        items={availableLessons}
        placeholder={'Select a lesson'}
        confirmLabel={'Add'}
        onConfirm={onPickLesson}
      />
      <CreateOrEditLessonModal
        isModalOpen={createLessonModal.isModalOpen}
        onModalClose={createLessonModal.onModalClose}
        modalState={{}}
        defaultValues={{ isDailyAlux: false, isQuestionAndAnswer: !!createLessonModal.modalState?.isQa }}
        onLessonCreated={onLessonCreated}
      />
      <CreateOrEditMentorShortModal
        isModalOpen={shortModal.isModalOpen}
        onModalClose={onShortModalClose}
        authors={authors}
        defaultAuthorId={authorId}
        lockAuthor
      />
    </>
  );
};

export default CreateOrEditAuthorModal;
